import { useEffect, useMemo, useState } from "react";

import { Plus, Search, Upload } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  useAccounts,
  useCreateAccount,
  useDeleteAccount,
  useToggleAccountActive,
  useUpdateAccount,
} from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import type {
  Account,
  AccountFormData,
  AccountType,
} from "~/types/contabilidad";
import { ACCOUNT_TYPE_LABELS } from "~/types/contabilidad";

import { CuentaDialog } from "./components/CuentaDialog";
import { CuentaImportDialog } from "./components/CuentaImportDialog";
import { CuentaTable } from "./components/CuentaTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function CuentasPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>(
    undefined
  );

  const { data: companies = [] } = useCompanies();
  const { data: accounts = [], isLoading } = useAccounts();

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
  }, [companies]);

  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();
  const deleteMutation = useDeleteAccount();
  const toggleActiveMutation = useToggleAccountActive();

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const matchesSearch =
        !searchQuery ||
        account.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.code.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCompany =
        companyFilter === "all" || account.company_id === companyFilter;

      const matchesType =
        typeFilter === "all" || account.account_type === typeFilter;

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && account.is_active) ||
        (statusFilter === "inactive" && !account.is_active);

      return matchesSearch && matchesCompany && matchesType && matchesStatus;
    });
  }, [accounts, searchQuery, companyFilter, typeFilter, statusFilter]);

  const handleCreate = () => {
    setEditingAccount(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setDialogOpen(true);
  };

  const handleSubmit = (data: AccountFormData) => {
    if (editingAccount) {
      updateMutation.mutate(
        { id: editingAccount.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingAccount(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleToggleActive = (id: string, isActive: boolean) => {
    toggleActiveMutation.mutate({ id, isActive });
  };

  const accountTypes: AccountType[] = [
    "activo",
    "pasivo",
    "patrimonio",
    "ingreso",
    "gasto",
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Cuentas"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
              className="rounded-full"
            >
              <Upload className="mr-2 h-4 w-4" />
              Importar
            </Button>
            <Button onClick={handleCreate} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Nueva Cuenta
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative h-9 w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por código o nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 rounded-xl pl-9"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="h-9 w-full rounded-xl sm:w-auto sm:min-w-[180px]">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9 w-full rounded-xl sm:w-auto sm:min-w-[160px]">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {accountTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {ACCOUNT_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-full rounded-xl sm:w-auto sm:min-w-[140px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="inactive">Inactivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <CuentaTable
              accounts={filteredAccounts}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          </div>
        </CardContent>
      </Card>

      <CuentaDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        account={editingAccount}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <CuentaImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </PageContainer>
  );
}
